import * as React from 'react';
import styled from 'styled-components';
import {
  alignItems,
  border,
  borderRadius,
  boxShadow,
  color,
  display,
  height,
  justifyContent,
  maxWidth,
  minWidth,
  space,
  width,
} from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { StyledSystemProps } from '../../theme/StyledSystemProps';
import { device } from '../../utils/device';
import Loader from './Loader';

interface ButtonProps extends StyledSystemProps {
  id?: string;
  text?: string;
  size?: string;
  color?: string;
  hover?: boolean;
  isIcon?: boolean;
  minimal?: boolean;
  outline?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  iconLeft?: React.ReactNode;
  icon?: React.ReactNode;
  style?: object;
  type: 'button' | 'submit' | 'reset';
  isWeb?: boolean;
}

const StyledButton = styled.button`
  ${space};
  ${width};
  ${color};
  ${border};
  ${height};
  ${(props: ButtonProps) => (props.hidden ? 'none' : display)};
  ${maxWidth};
  ${alignItems};
  ${borderRadius};
  ${justifyContent};
  ${(props: ButtonProps) => !props.isIcon && minWidth};
  ${(props: ButtonProps) => !props.minimal && boxShadow};
  cursor: pointer;
  &:hover {
    box-shadow: ${(props: ButtonProps) =>
      props.bg === 'transparent' || props.minimal ? 'none' : '0 1px 4px rgba(50,50,93,0.4)'};
    background: ${(props: ButtonProps) => props.minimal && 'rgba(50,50,93,0.05)'};
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform: ${(props: ButtonProps) => props.hover && 'translateY(-1px)'};
    cursor: pointer;
  }
  &:disabled {
    opacity: 0.6;
  }

  i,
  i > svg {
    height: ${themeGet('space[2]', '18px')};
    width: ${themeGet('space[2]', '18px')};
  }
  font-weight: ${(props: ButtonProps) => !props.isWeb && 600};
  letter-spacing: ${(props: ButtonProps) => !props.isWeb && '0.1em'};
  @media ${device.mobileS} {
    font-size: ${(props: ButtonProps) => !props.isWeb && '0.7em'};
  }
  @media ${device.mobileM} {
    font-size: ${(props: ButtonProps) => !props.isWeb && '0.7em'};
  }
  @media ${device.mobileL} {
    font-size: ${(props: ButtonProps) => !props.isWeb && '0.9em'};
  }
  @media ${device.tablet} {
    font-size: ${(props: ButtonProps) => !props.isWeb && '1em'};
  }
`;

const Button: React.FC<ButtonProps> = props => {
  return (
    <StyledButton disabled={props.isLoading} {...props} justifyContent="space-between">
      {props.isLoading ? <Loader width={20} color={props.color} height={20} /> : !props.isIcon && props.text}
      {props.icon && <i>{props.icon}</i>}
    </StyledButton>
  );
};

Button.defaultProps = {
  alignItems: 'center',
  iconLeft: null,
  icon: null,
  size: 'md',
  bg: 'primary',
  color: '#FFF',
  display: 'flex',
  flex: '1',
  width: '100%',
  outline: false,
  height: '50px',
  minimal: false,
  text: 'Button',
  isLoading: false,
  p: '0.25em 1.2em',
  minWidth: '150px',
  onClick: () => null,
  borderRadius: '4px',
  justifyContent: '',
  boxShadow: '0 3px 10px rgba(50,50,93,0.2)',
};

export default Button;
